import { SlArrowLeft } from 'react-icons/sl';
import { FaCamera } from 'react-icons/fa';
import {
  useState, useEffect, useRef, ChangeEvent,
} from 'react';
import { IoSearchOutline } from 'react-icons/io5';
import {
  FiSettings, FiSun, FiUsers, FiList,
} from 'react-icons/fi';
import useNavigateWithQuery from '../../hooks/useNavigateWithQuery';
import Switch from './Switch';
import Select from './Select/Select';
import EventDTO from '../../models/event';

interface Props {
  onSearchClick?: () => void;
  onCameraClick?: (deviceId: string) => void;
  onListClick?: () => void;
  capacity: number | undefined;
  event: EventDTO | null;
}

const QRSubheader: React.FC<Props> = ({
  onSearchClick,
  onCameraClick,
  onListClick,
  capacity,
  event,
}) => {
  const navigate = useNavigateWithQuery();
  const [cameras, setCameras] = useState<{ label: string; deviceId: string }[]>([]);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [keepScreenOn, setKeepScreenOn] = useState(false);
  const wakeLock = useRef<WakeLockSentinel | null>(null);
  const settingsRef = useRef<HTMLDivElement>(null);
  const settingsIconRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        settingsRef.current
        && !settingsRef.current.contains(e.target as Node)
        && settingsIconRef.current
        && !settingsIconRef.current.contains(e.target as Node)
      ) {
        setShowSettings(false);
      }
    };

    if (showSettings) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSettings]);

  const handleKeepScreenOnChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setKeepScreenOn(checked);
    if (checked) {
      try {
        wakeLock.current = await navigator.wakeLock.request('screen');
        wakeLock.current.onrelease = () => {
          setKeepScreenOn(false);
        };
      } catch (err) {
        console.error('Failed to get wake lock:', err);
        setKeepScreenOn(false);
      }
    } else if (wakeLock.current) {
      wakeLock.current.release();
      wakeLock.current = null;
    }
  };

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices()
      .then((devices) => {
        const videoDevices = devices.filter((device) => device.kind === 'videoinput');
        const cameraOptions = videoDevices.map((device) => ({
          label: device.label || `Camera ${videoDevices.indexOf(device) + 1}`,
          deviceId: device.deviceId,
        }));
        setCameras(cameraOptions);
        if (cameraOptions.length > 0 && !selectedCamera) {
          const firstCamera = cameraOptions[0].deviceId;
          setSelectedCamera(firstCamera);
          onCameraClick?.(firstCamera);
        }
      })
      .catch((err) => {
        console.error('Error getting cameras:', err);
      });

    return () => {
      if (wakeLock.current) {
        wakeLock.current.release();
      }
    };
  }, [onCameraClick, selectedCamera]);

  const handleCameraChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const deviceId = e.target.value;
    setSelectedCamera(deviceId);
    onCameraClick?.(deviceId);
  };

  return (
    <>
      <div className="h-18 w-full grid grid-cols-3 items-center bg-surface-base p-4 text-text-primary">
        <SlArrowLeft onClick={() => navigate('..')} />
        <div className='flex items-center gap-2 whitespace-nowrap justify-center' >
          <div className="font-medium">Escáner QR</div>
          <div ref={settingsIconRef} className='flex items-center gap-2'>
            <FiSettings className='h-5 w-5 cursor-pointer' onClick={() => setShowSettings(!showSettings)} />
          </div>
        </div>
        <div className='flex items-center gap-2 whitespace-nowrap justify-end'>
          {event?.has_lists && <FiList className='h-5 w-5 cursor-pointer' onClick={onListClick} />}
          <IoSearchOutline className='h-5 w-5 cursor-pointer' onClick={onSearchClick} />
        </div>
      </div>
      {showSettings && (
        <div ref={settingsRef} className='absolute top-[54px] lg:top-14 left-0 lg:pl-[280px] w-full z-10'>
          <div className='flex flex-col gap-4 bg-surface-base shadow-sm p-2'>
            <div className='flex items-center gap-4'>
              <FaCamera className='h-5 w-5' />
              <Select
                id="camera-select"
                options={[
                  ...cameras.map((camera) => ({
                    label: camera.label,
                    value: camera.deviceId,
                  })),
                ]}
                value={selectedCamera}
                onChange={handleCameraChange}
              />
            </div>
            <div className='flex items-center gap-4 py-3'>
              <FiSun className='h-5 w-5' />
              <p>Mantener pantalla encendida</p>
              <div className='ml-auto'>
                <Switch
                  checked={keepScreenOn}
                  onChange={handleKeepScreenOnChange}
                />
              </div>
            </div>
            <div className='flex items-center gap-4 py-3'>
              <FiUsers className='h-5 w-5' />
              <p>Cantidad de escaneos: {capacity ?? 0} códigos</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QRSubheader;
